import { type FC, useRef, useEffect } from "react";
import Chart from "chart.js/auto";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

type DataChartProps = {
  title: string;
  labels: string[];
  values: number[];
  unit: string;
};

export const DeviceDataChart: FC<DataChartProps> = ({
  title,
  labels,
  values,
  unit,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (ctx) {
      chartInstanceRef.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: labels,
          datasets: [
            {
              label: t('chart.dataset_label', { unit }),
              data: values,
              borderColor: "#22d3ee",
              backgroundColor: "rgba(34, 211, 238, 0.12)",
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 5,
              tension: 0.35,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              title: {
                display: true,
                text: t('chart.x_title'),
                color: "#9ca3af",
                font: {
                  size: 12,
                },
              },
              grid: {
                color: "#374151",
              },
              ticks: {
                color: "#d1d5db",
              },
            },
            y: {
              title: {
                display: true,
                text: t('chart.y_title', { unit }),
                color: "#9ca3af",
                font: {
                  size: 12,
                },
              },
              grid: {
                color: "#374151",
              },
              ticks: {
                color: "#d1d5db",
              },
            },
          },
          plugins: {
            legend: {
              labels: {
                color: "#e5e7eb",
              },
            },
            tooltip: {
              backgroundColor: "#1f2937",
              titleColor: "#22d3ee",
              bodyColor: "#f3f4f6",
              borderColor: "#22d3ee",
              borderWidth: 1,
            },
          },
        },
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.data.labels = labels;
      chartInstanceRef.current.data.datasets[0].data = values;
      chartInstanceRef.current.data.datasets[0].label = t('chart.dataset_label', { unit });
      chartInstanceRef.current.update();
    }
  }, [labels, values, unit, t]);

  return (
    <motion.div
      className="bg-gradient-to-br w-full from-gray-800 to-gray-900 p-6 rounded-2xl shadow-[0_0_30px_-10px_rgba(0,0,0,0.6)] border border-gray-700"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-cyan-300 drop-shadow-sm">
          {title}
        </h3>
      </div>

      <div className="relative w-full h-[320px] bg-gray-800/40 rounded-xl border border-gray-700 p-2">
        <canvas ref={canvasRef}></canvas>
      </div>
    </motion.div>
  );
};
